import unityLearningIcon from "../../images/andus-system-router-unity-learning-icon.svg";
import talentLenzIcon from "../../images/andus-system-router-talentlenz-icon.svg";
import financialDashboardIcon from "../assets/financial-dashboard-icon.svg";
import fleetCoreIcon from "../../images/andus-system-router-fleetcore-icon.svg";
import preAuditIcon from "../../preAudit/assets/pre-audit-icon.svg";
import { PRE_AUDIT_BASE_ROUTE } from "../../preAudit/preAuditConfig";

export const PRODUCT_CATALOGUE = Object.freeze({
  unityLearning: {
    key: "unityLearning",
    displayName: "Unity Learning",
    description: "Learning delivery, progress and programme management.",
    launchRoute: "/unity",
    icon: unityLearningIcon,
    tone: "blue",
  },
  talentLenz: {
    key: "talentLenz",
    displayName: "TalentLenz",
    description: "Opportunity-specific applicant assessment and shortlisting.",
    launchRoute: "/talentlenz/workspace/view",
    icon: talentLenzIcon,
    tone: "pink",
  },
  financialDashboard: {
    key: "financialDashboard",
    displayName: "Financial Dashboard",
    description: "Claims, supplier workflows and financial oversight.",
    launchRoute: "/financialDashboard",
    icon: financialDashboardIcon,
    tone: "blue",
  },
  fleetCore: {
    key: "fleetCore",
    displayName: "FleetCore",
    description: "Fleet evidence, vehicle condition and fuel intelligence.",
    launchRoute: "/fleetcore/dashboard",
    icon: fleetCoreIcon,
    tone: "blue",
  },
  preAudit: {
    key: "preAudit",
    displayName: "Supplier Audit System",
    description: "Configure audit steps and manage supplier evidence and reviews.",
    launchRoute: PRE_AUDIT_BASE_ROUTE,
    icon: preAuditIcon,
    tone: "blue",
  },
});

export const PRODUCT_KEYS = Object.freeze(Object.keys(PRODUCT_CATALOGUE));

const ROLE_LABELS = Object.freeze({
  tenant_admin: "Tenant administrator",
  tenant_user: "Tenant user",
  platform_owner: "Platform owner",
  platform_admin: "Platform administrator",
});

export function formatAccessRole(role, fallback = "Not assigned") {
  const value = String(role || "").trim();
  if (!value) return fallback;
  if (ROLE_LABELS[value]) return ROLE_LABELS[value];
  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());
}

export function formatSystemRoles(roles, fallback = "Not assigned") {
  const values = Array.isArray(roles)
    ? Array.from(new Set(roles.map((role) => String(role || "").trim()).filter(Boolean)))
    : [];
  return values.length
    ? values.map((role) => formatAccessRole(role)).join(", ")
    : fallback;
}

export function formatContentAuthoringMode() {
  // Historical rollout settings no longer determine authoring availability.
  // Programme permissions are still checked by the authenticated service.
  return "Read and build";
}

export function normaliseSystem(system, tenant) {
  const key = system?.key;
  const catalogueEntry = PRODUCT_CATALOGUE[key];
  if (!catalogueEntry) return null;

  const assignedRoles = Array.isArray(system.roles)
    ? system.roles
    : Array.isArray(system.systemRoles)
      ? system.systemRoles
      : [];
  const systemRole = assignedRoles.length
    ? formatSystemRoles(assignedRoles)
    : formatAccessRole(system.role);
  const workspaceRole = formatAccessRole(tenant?.tenantRole, "Tenant member");
  const contentIntegrationMode = key === "unityLearning"
    ? "tenant_v2"
    : null;
  const opportunityOnly = key === "talentLenz" &&
    assignedRoles.length === 1 &&
    assignedRoles.includes("opportunity_user");
  const preAuditLaunchRole = ["admin", "reviewer", "supplier"]
    .find((role) => assignedRoles.includes(role));
  const launchRoute = opportunityOnly
    ? "/opportunity-page"
    : key === "talentLenz"
      ? catalogueEntry.launchRoute
    : key === "preAudit"
      ? `${catalogueEntry.launchRoute}/${preAuditLaunchRole || ""}`.replace(/\/$/, "")
    : system.launchRoute || catalogueEntry.launchRoute;

  return {
    ...catalogueEntry,
    ...system,
    key,
    displayName: key === "preAudit"
      ? catalogueEntry.displayName
      : system.displayName || catalogueEntry.displayName,
    description: system.description || catalogueEntry.description,
    launchRoute,
    assignedRoles,
    role: systemRole,
    systemRole,
    workspaceRole,
    ...(contentIntegrationMode ? {
      contentIntegrationMode,
      contentAuthoringStatus: formatContentAuthoringMode(contentIntegrationMode),
    } : {}),
    workspace: system.workspace || tenant?.displayName || tenant?.company || "AndUs workspace",
  };
}

export function getProductForPath(pathname) {
  const path = String(pathname || "").toLowerCase();
  if (path === "/pre-audit" || path.startsWith("/pre-audit/")) {
    return { productKey: "preAudit" };
  }
  if (path.startsWith("/opportunity-page")) {
    return { productKey: "talentLenz" };
  }
  if (path.startsWith("/talentlenz/workspace")) {
    return { productKey: "talentLenz" };
  }
  if (path.startsWith("/hero-dashboard/opportunities")) {
    return { productKey: "talentLenz" };
  }
  if (
    path.startsWith("/financialdashboard") ||
    path.startsWith("/hero-dashboard/financialdashboard") ||
    path.startsWith("/hero-dashboard/claims") ||
    path.startsWith("/hero-dashboard/suppliers") ||
    path.startsWith("/hero-dashboard/programfees") ||
    path.startsWith("/hero-dashboard/program-fees")
  ) {
    return { productKey: "financialDashboard" };
  }
  if (
    path.startsWith("/hero-dashboard/learners") ||
    path.startsWith("/hero-dashboard/facilitators") ||
    path.startsWith("/hero-dashboard/learnerunityprogress") ||
    path.startsWith("/hero-dashboard/intake-block") ||
    path.startsWith("/hero-dashboard/bursary-learners")
  ) {
    return { productKey: "unityLearning", moduleKey: "studentAdministration" };
  }
  if (path.startsWith("/hero-dashboard/unity-learning")) {
    return { productKey: "unityLearning" };
  }
  if (
    path.startsWith("/hero-dashboard/view-content/") ||
    path.startsWith("/hero-dashboard/create-concept") ||
    path.startsWith("/hero-dashboard/build-program") ||
    path.startsWith("/hero-dashboard/explore-") ||
    path.startsWith("/hero-dashboard/learningtestsystem") ||
    path.startsWith("/hero-dashboard/aicontentbulder") ||
    path.startsWith("/hero-dashboard/unitcontentcreator")
  ) {
    return { productKey: "unityLearning", moduleKey: "learning" };
  }
  if (path.startsWith("/fleetcore/")) {
    return { productKey: "fleetCore" };
  }
  return null;
}
